import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import budgetPlanningService from '../../../services/budgetPlanningService'
import projectService from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'
import autoIncrementService from '../../../services/autoIncrementService'
import { 
  DollarSign,
  Calendar,
  FileText,
  AlertCircle,
  FlaskConical,
  HelpCircle
} from 'lucide-react'

const BudgetPlanningForm = ({ mode = 'create', budgetId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [budgetDataLoading, setBudgetDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [isLoadingBudgetNumber, setIsLoadingBudgetNumber] = useState(false)
  const hasLoadedBudgetDataRef = useRef(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode

  const [masterData, setMasterData] = useState({
    projects: [],
    employees: []
  })

  const [formData, setFormData] = useState({
    budgetNumber: '',
    budgetName: '',
    budgetDescription: '',
    project: '',
    budgetOwner: '',
    budgetPeriod: {
      startDate: '',
      endDate: '',
      fiscalYear: '',
      quarter: ''
    },
    status: 'Draft',
    budgetedAmounts: {
      labor: 0,
      materials: 0,
      equipment: 0,
      travel: 0,
      overhead: 0,
      other: 0,
      contingency: 0
    },
    actualAmounts: {
      labor: 0,
      materials: 0,
      equipment: 0,
      travel: 0,
      overhead: 0,
      other: 0
    },
    currency: 'INR',
    requiresApproval: true,
    budgetThresholds: {
      warningThreshold: 80,
      criticalThreshold: 95,
      notifyOnThreshold: true
    },
    notes: ''
  })

  const [errors, setErrors] = useState({})

  // Load master data on component mount
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
  }, [masterDataLoaded, loading])

  // Fetch next budget number (only for new budgets)
  useEffect(() => {
    if (!isEditMode && !isViewMode && !isLoadingBudgetNumber) {
      fetchNextBudgetNumber()
    }
  }, [isEditMode, isViewMode])

  // Load budget data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && budgetId && masterDataLoaded && !hasLoadedBudgetDataRef.current) {
      if (initialData) {
        populateFormData(initialData)
        hasLoadedBudgetDataRef.current = true
      } else if (!budgetDataLoading) {
        loadBudgetData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetId, isEditMode, isViewMode, masterDataLoaded])

  const loadBudgetData = async () => {
    if (hasLoadedBudgetDataRef.current) {
      return
    }
    
    try {
      setBudgetDataLoading(true)
      hasLoadedBudgetDataRef.current = true
      const response = await budgetPlanningService.getBudgetPlanningById(budgetId)
      
      if (response.success && response.data) {
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load budget planning')
        navigate('/projects/budget-plannings/list')
      }
    } catch (error) {
      console.error('Error loading budget planning:', error)
      toast.error('Failed to load budget planning')
      navigate('/projects/budget-plannings/list')
    } finally {
      setBudgetDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    // Extract IDs from populated objects
    let projectValue = ''
    if (data.project) {
      if (typeof data.project === 'object' && data.project !== null) {
        projectValue = String(data.project._id || data.project.id || '')
      } else {
        projectValue = String(data.project)
      }
    }

    let budgetOwnerValue = ''
    if (data.budgetOwner) {
      if (typeof data.budgetOwner === 'object' && data.budgetOwner !== null) {
        budgetOwnerValue = String(data.budgetOwner._id || data.budgetOwner.id || '')
      } else {
        budgetOwnerValue = String(data.budgetOwner)
      }
    }

    // Format dates
    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    }

    setFormData({
      budgetNumber: data.budgetNumber || '',
      budgetName: data.budgetName || '',
      budgetDescription: data.budgetDescription || '',
      project: projectValue,
      budgetOwner: budgetOwnerValue,
      budgetPeriod: {
        startDate: formatDate(data.budgetPeriod?.startDate),
        endDate: formatDate(data.budgetPeriod?.endDate),
        fiscalYear: data.budgetPeriod?.fiscalYear || '',
        quarter: data.budgetPeriod?.quarter || ''
      },
      status: data.status || 'Draft',
      budgetedAmounts: data.budgetedAmounts || {
        labor: 0,
        materials: 0,
        equipment: 0,
        travel: 0,
        overhead: 0,
        other: 0,
        contingency: 0
      },
      actualAmounts: data.actualAmounts || {
        labor: 0,
        materials: 0,
        equipment: 0,
        travel: 0,
        overhead: 0,
        other: 0
      },
      currency: data.currency || 'INR',
      requiresApproval: data.requiresApproval !== undefined ? data.requiresApproval : true,
      budgetThresholds: data.budgetThresholds || {
        warningThreshold: 80,
        criticalThreshold: 95,
        notifyOnThreshold: true
      },
      notes: data.notes || ''
    })
  }

  const fetchNextBudgetNumber = async () => {
    try {
      setIsLoadingBudgetNumber(true)
      const response = await autoIncrementService.getCurrentValue('BUDGET-PLANNING')
      if (response.success && response.data) {
        const sequence = response.data
        if (sequence.formattedValue) {
          setFormData(prev => ({
            ...prev,
            budgetNumber: sequence.formattedValue
          }))
        } else {
          setFormData(prev => ({
            ...prev,
            budgetNumber: 'BUD-000001'
          }))
        }
      } else {
        setFormData(prev => ({
          ...prev,
          budgetNumber: 'BUD-000001'
        }))
      }
    } catch (error) {
      console.error('Error fetching next budget number:', error)
      setFormData(prev => ({
        ...prev,
        budgetNumber: 'BUD-000001'
      }))
    } finally {
      setIsLoadingBudgetNumber(false)
    }
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectsRes,
        employeesRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000, status: 'Active' }),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' })
      ])

      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
          if (res.projects) {
            return res.projects
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      setMasterData({
        projects: getData(projectsRes),
        employees: getEmployeesData(employeesRes)
      })

      const failedRequests = [
        { name: 'Projects', result: projectsRes },
        { name: 'Employees', result: employeesRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 2) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    if (field.startsWith('budgetPeriod.')) {
      const periodField = field.replace('budgetPeriod.', '')
      setFormData(prev => ({
        ...prev,
        budgetPeriod: {
          ...prev.budgetPeriod,
          [periodField]: value
        }
      }))
    } else if (field.startsWith('budgetedAmounts.')) {
      const amountField = field.replace('budgetedAmounts.', '')
      setFormData(prev => ({
        ...prev,
        budgetedAmounts: {
          ...prev.budgetedAmounts,
          [amountField]: parseFloat(value) || 0
        }
      }))
    } else if (field.startsWith('actualAmounts.')) {
      const amountField = field.replace('actualAmounts.', '')
      setFormData(prev => ({
        ...prev,
        actualAmounts: {
          ...prev.actualAmounts,
          [amountField]: parseFloat(value) || 0
        }
      }))
    } else if (field.startsWith('budgetThresholds.')) {
      const thresholdField = field.replace('budgetThresholds.', '')
      setFormData(prev => ({
        ...prev,
        budgetThresholds: {
          ...prev.budgetThresholds,
          [thresholdField]: thresholdField === 'notifyOnThreshold' ? value : (parseFloat(value) || 0)
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.budgetName || formData.budgetName.trim() === '') {
      newErrors.budgetName = 'Budget name is required'
    }
    if (!formData.project) {
      newErrors.project = 'Project is required'
    }
    if (!formData.budgetOwner) {
      newErrors.budgetOwner = 'Budget owner is required'
    }
    if (!formData.budgetPeriod.startDate) {
      newErrors['budgetPeriod.startDate'] = 'Start date is required'
    }
    if (!formData.budgetPeriod.endDate) {
      newErrors['budgetPeriod.endDate'] = 'End date is required'
    }
    if (formData.budgetPeriod.startDate && formData.budgetPeriod.endDate && 
        new Date(formData.budgetPeriod.startDate) > new Date(formData.budgetPeriod.endDate)) {
      newErrors['budgetPeriod.endDate'] = 'End date must be after start date'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    try {
      setLoading(true)

      let response
      if (isEditMode) {
        const { budgetNumber, ...updateData } = formData
        response = await budgetPlanningService.updateBudgetPlanning(budgetId, updateData)
      } else {
        const { budgetNumber, ...createData } = formData
        response = await budgetPlanningService.createBudgetPlanning(createData)
      }

      if (response.success) {
        toast.success(`Budget planning ${isEditMode ? 'updated' : 'created'} successfully`)
        navigate('/projects/budget-plannings/list')
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} budget planning`)
      }
    } catch (error) {
      console.error('Error saving budget planning:', error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} budget planning`)
    } finally {
      setLoading(false)
    }
  }

  const handleTestData = () => {
    const today = new Date()
    const endDate = new Date(today)
    endDate.setMonth(today.getMonth() + 3)

    setFormData(prev => ({
      ...prev,
      budgetNumber: prev.budgetNumber, // Preserve existing budgetNumber
      budgetName: 'Q1 2025 Budget Plan',
      budgetDescription: 'Budget plan for Q1 2025 covering all project expenses',
      project: masterData.projects.length > 0 ? (masterData.projects[0]._id || masterData.projects[0].id) : '',
      budgetOwner: masterData.employees.length > 0 ? (masterData.employees[0]._id || masterData.employees[0].id) : '',
      budgetPeriod: {
        startDate: today.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        fiscalYear: '2025',
        quarter: 'Q1'
      },
      status: 'Draft',
      budgetedAmounts: {
        labor: 500000,
        materials: 100000,
        equipment: 50000,
        travel: 75000,
        overhead: 100000,
        other: 25000,
        contingency: 50000
      },
      actualAmounts: {
        labor: 0,
        materials: 0,
        equipment: 0,
        travel: 0,
        overhead: 0,
        other: 0
      },
      currency: 'INR',
      requiresApproval: true,
      budgetThresholds: {
        warningThreshold: 80,
        criticalThreshold: 95,
        notifyOnThreshold: true
      },
      notes: 'Test budget planning data'
    }))
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Budget Plannings', path: '/projects/budget-plannings/list', onClick: () => navigate('/projects/budget-plannings/list') },
    { label: isEditMode ? 'Edit Budget Plan' : isViewMode ? 'View Budget Plan' : 'Create New', path: isEditMode ? `/projects/budget-plannings/edit/${budgetId}` : isViewMode ? `/projects/budget-plannings/view/${budgetId}` : '/projects/budget-plannings/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FileText },
    { id: 'amounts', label: 'Budget Amounts', icon: DollarSign },
    { id: 'additional', label: 'Additional Info', icon: AlertCircle }
  ]

  const helpContent = (
    <>
      <h3>What is Budget Planning Entry?</h3>
      <p>Budget Planning Entry lets you <strong>create and manage project budgeting and cost tracking</strong>. You link a budget plan to a project, set budgeted amounts by category, track actuals, and monitor variance and utilization.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Budget plan number, project, plan name, period, currency, status.</p>
      <p><strong>Budget Amounts</strong> – Budgeted and actual amounts by category; totals and variance.</p>
      <p><strong>Additional Info</strong> – Notes and revision history.</p>
      <h3>How to use</h3>
      <p>Select a <strong>project</strong>, enter plan name and period. Add budgeted amounts in Budget Amounts. Use <strong>Save</strong> to create or update. Track actuals and compare to plan.</p>
    </>
  )

  // Calculate totals
  const totalBudgeted = Object.values(formData.budgetedAmounts || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
  const totalActual = Object.values(formData.actualAmounts || {}).reduce((sum, val) => sum + (parseFloat(val) || 0), 0)
  const totalVariance = totalActual - totalBudgeted
  const variancePercentage = totalBudgeted > 0 ? ((totalVariance / totalBudgeted) * 100).toFixed(2) : 0
  const utilizationPercentage = totalBudgeted > 0 ? ((totalActual / totalBudgeted) * 100).toFixed(2) : 0

  if (budgetDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading budget planning data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Budget Planning Entry</h1>
          <p>Create and manage budget planning information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Budget Planning Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} style={{ color: '#DC2626' }} />
                <strong style={{ color: '#DC2626' }}>Please fix the following errors:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626' }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    budgetName: 'Budget Name',
                    project: 'Project',
                    budgetOwner: 'Budget Owner',
                    'budgetPeriod.startDate': 'Start Date',
                    'budgetPeriod.endDate': 'End Date'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <div>
                <div className="section-header">
                  <h3>Basic Information</h3>
                </div>
                
                {/* Row 1: Budget Number, Budget Name, Project */}
                <div className="grid-row-3">
                  {/* Budget Number */}
                  <div className="form-group">
                    <label htmlFor="budgetNumber" className="form-label">
                      Budget Number
                    </label>
                    <input
                      id="budgetNumber"
                      type="text"
                      value={isLoadingBudgetNumber ? 'Loading...' : formData.budgetNumber}
                      className="form-input"
                      placeholder="Auto-generated"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  {/* Budget Name */}
                  <div className="form-group">
                    <label htmlFor="budgetName" className="form-label required">
                      Budget Name
                    </label>
                    <input
                      id="budgetName"
                      type="text"
                      value={formData.budgetName}
                      onChange={(e) => handleInputChange('budgetName', e.target.value)}
                      className="form-input"
                      placeholder="Enter budget name"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.budgetName && (
                      <p className="form-error">{errors.budgetName}</p>
                    )}
                  </div>

                  {/* Project */}
                  <div className="form-group">
                    <label htmlFor="project" className="form-label required">
                      Project
                    </label>
                    <select
                      id="project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project</option>
                      {masterData.projects.map((project) => {
                        const projectId = project._id || project.id
                        return (
                          <option key={projectId} value={String(projectId)}>
                            {project.projectNumber} - {project.projectName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.project && (
                      <p className="form-error">{errors.project}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Budget Description */}
                <div className="form-group">
                  <label htmlFor="budgetDescription" className="form-label">
                    Budget Description
                  </label>
                  <textarea
                    id="budgetDescription"
                    value={formData.budgetDescription}
                    onChange={(e) => handleInputChange('budgetDescription', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter budget description"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Row 3: Budget Owner, Status, Currency */}
                <div className="grid-row-3">
                  {/* Budget Owner */}
                  <div className="form-group">
                    <label htmlFor="budgetOwner" className="form-label required">
                      Budget Owner
                    </label>
                    <select
                      id="budgetOwner"
                      value={formData.budgetOwner}
                      onChange={(e) => handleInputChange('budgetOwner', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Employee</option>
                      {masterData.employees.map((employee) => {
                        const employeeId = employee._id || employee.id
                        return (
                          <option key={employeeId} value={String(employeeId)}>
                            {employee.employeeName || employee.name}
                          </option>
                        )
                      })}
                    </select>
                    {errors.budgetOwner && (
                      <p className="form-error">{errors.budgetOwner}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Submitted">Submitted</option>
                      <option value="Under Review">Under Review</option>
                      <option value="Approved">Approved</option>
                      <option value="Active">Active</option>
                      <option value="Closed">Closed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </div>

                  {/* Currency */}
                  <div className="form-group">
                    <label htmlFor="currency" className="form-label">
                      Currency
                    </label>
                    <select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => handleInputChange('currency', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="CAD">CAD</option>
                      <option value="AUD">AUD</option>
                    </select>
                  </div>
                </div>

                {/* Row 4: Budget Period */}
                <div className="section-header">
                  <h3>Budget Period</h3>
                </div>
                <div className="grid-row-3">
                  {/* Start Date */}
                  <div className="form-group">
                    <label htmlFor="budgetPeriod.startDate" className="form-label required">
                      Start Date
                    </label>
                    <input
                      id="budgetPeriod.startDate"
                      type="date"
                      value={formData.budgetPeriod.startDate}
                      onChange={(e) => handleInputChange('budgetPeriod.startDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors['budgetPeriod.startDate'] && (
                      <p className="form-error">{errors['budgetPeriod.startDate']}</p>
                    )}
                  </div>

                  {/* End Date */}
                  <div className="form-group">
                    <label htmlFor="budgetPeriod.endDate" className="form-label required">
                      End Date
                    </label>
                    <input
                      id="budgetPeriod.endDate"
                      type="date"
                      value={formData.budgetPeriod.endDate}
                      onChange={(e) => handleInputChange('budgetPeriod.endDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors['budgetPeriod.endDate'] && (
                      <p className="form-error">{errors['budgetPeriod.endDate']}</p>
                    )}
                  </div>

                  {/* Quarter */}
                  <div className="form-group">
                    <label htmlFor="budgetPeriod.quarter" className="form-label">
                      Quarter
                    </label>
                    <select
                      id="budgetPeriod.quarter"
                      value={formData.budgetPeriod.quarter}
                      onChange={(e) => handleInputChange('budgetPeriod.quarter', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Quarter</option>
                      <option value="Q1">Q1</option>
                      <option value="Q2">Q2</option>
                      <option value="Q3">Q3</option>
                      <option value="Q4">Q4</option>
                    </select>
                  </div>
                </div>

                {/* Fiscal Year */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="budgetPeriod.fiscalYear" className="form-label">
                      Fiscal Year
                    </label>
                    <input
                      id="budgetPeriod.fiscalYear"
                      type="text"
                      value={formData.budgetPeriod.fiscalYear}
                      onChange={(e) => handleInputChange('budgetPeriod.fiscalYear', e.target.value)}
                      className="form-input"
                      placeholder="e.g., 2025"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'amounts' && (
              <div>
                <div className="section-header">
                  <h3>Budget Amounts</h3>
                </div>
                
                {/* Budgeted Amounts */}
                <div className="section-header" style={{ marginTop: '16px', marginBottom: '8px' }}>
                  <h4>Budgeted Amounts</h4>
                </div>
                <div className="grid-row-3">
                  {/* Labor */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.labor" className="form-label">
                      Labor
                    </label>
                    <input
                      id="budgetedAmounts.labor"
                      type="number"
                      value={formData.budgetedAmounts.labor}
                      onChange={(e) => handleInputChange('budgetedAmounts.labor', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Materials */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.materials" className="form-label">
                      Materials
                    </label>
                    <input
                      id="budgetedAmounts.materials"
                      type="number"
                      value={formData.budgetedAmounts.materials}
                      onChange={(e) => handleInputChange('budgetedAmounts.materials', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Equipment */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.equipment" className="form-label">
                      Equipment
                    </label>
                    <input
                      id="budgetedAmounts.equipment"
                      type="number"
                      value={formData.budgetedAmounts.equipment}
                      onChange={(e) => handleInputChange('budgetedAmounts.equipment', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                <div className="grid-row-3">
                  {/* Travel */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.travel" className="form-label">
                      Travel
                    </label>
                    <input
                      id="budgetedAmounts.travel"
                      type="number"
                      value={formData.budgetedAmounts.travel}
                      onChange={(e) => handleInputChange('budgetedAmounts.travel', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Overhead */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.overhead" className="form-label">
                      Overhead
                    </label>
                    <input
                      id="budgetedAmounts.overhead"
                      type="number"
                      value={formData.budgetedAmounts.overhead}
                      onChange={(e) => handleInputChange('budgetedAmounts.overhead', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Other */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.other" className="form-label">
                      Other
                    </label>
                    <input
                      id="budgetedAmounts.other"
                      type="number"
                      value={formData.budgetedAmounts.other}
                      onChange={(e) => handleInputChange('budgetedAmounts.other', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                <div className="grid-row-3">
                  {/* Contingency */}
                  <div className="form-group">
                    <label htmlFor="budgetedAmounts.contingency" className="form-label">
                      Contingency
                    </label>
                    <input
                      id="budgetedAmounts.contingency"
                      type="number"
                      value={formData.budgetedAmounts.contingency}
                      onChange={(e) => handleInputChange('budgetedAmounts.contingency', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Total Budgeted */}
                  <div className="form-group">
                    <label className="form-label">
                      Total Budgeted
                    </label>
                    <input
                      type="text"
                      value={totalBudgeted.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      className="form-input"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', fontWeight: 'bold' }}
                    />
                  </div>
                </div>

                {/* Actual Amounts */}
                <div className="section-header" style={{ marginTop: '24px', marginBottom: '8px' }}>
                  <h4>Actual Amounts</h4>
                </div>
                <div className="grid-row-3">
                  {/* Labor */}
                  <div className="form-group">
                    <label htmlFor="actualAmounts.labor" className="form-label">
                      Labor
                    </label>
                    <input
                      id="actualAmounts.labor"
                      type="number"
                      value={formData.actualAmounts.labor}
                      onChange={(e) => handleInputChange('actualAmounts.labor', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Materials */}
                  <div className="form-group">
                    <label htmlFor="actualAmounts.materials" className="form-label">
                      Materials
                    </label>
                    <input
                      id="actualAmounts.materials"
                      type="number"
                      value={formData.actualAmounts.materials}
                      onChange={(e) => handleInputChange('actualAmounts.materials', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Equipment */}
                  <div className="form-group">
                    <label htmlFor="actualAmounts.equipment" className="form-label">
                      Equipment
                    </label>
                    <input
                      id="actualAmounts.equipment"
                      type="number"
                      value={formData.actualAmounts.equipment}
                      onChange={(e) => handleInputChange('actualAmounts.equipment', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                <div className="grid-row-3">
                  {/* Travel */}
                  <div className="form-group">
                    <label htmlFor="actualAmounts.travel" className="form-label">
                      Travel
                    </label>
                    <input
                      id="actualAmounts.travel"
                      type="number"
                      value={formData.actualAmounts.travel}
                      onChange={(e) => handleInputChange('actualAmounts.travel', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Overhead */}
                  <div className="form-group">
                    <label htmlFor="actualAmounts.overhead" className="form-label">
                      Overhead
                    </label>
                    <input
                      id="actualAmounts.overhead"
                      type="number"
                      value={formData.actualAmounts.overhead}
                      onChange={(e) => handleInputChange('actualAmounts.overhead', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Other */}
                  <div className="form-group">
                    <label htmlFor="actualAmounts.other" className="form-label">
                      Other
                    </label>
                    <input
                      id="actualAmounts.other"
                      type="number"
                      value={formData.actualAmounts.other}
                      onChange={(e) => handleInputChange('actualAmounts.other', e.target.value)}
                      className="form-input"
                      min="0"
                      step="0.01"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="section-header" style={{ marginTop: '24px', marginBottom: '8px' }}>
                  <h4>Budget Summary</h4>
                </div>
                <div className="grid-row-3">
                  {/* Total Actual */}
                  <div className="form-group">
                    <label className="form-label">
                      Total Actual
                    </label>
                    <input
                      type="text"
                      value={totalActual.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      className="form-input"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', fontWeight: 'bold' }}
                    />
                  </div>

                  {/* Variance */}
                  <div className="form-group">
                    <label className="form-label">
                      Variance
                    </label>
                    <input
                      type="text"
                      value={totalVariance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      className="form-input"
                      readOnly
                      disabled
                      style={{ 
                        backgroundColor: '#f3f4f6', 
                        cursor: 'not-allowed', 
                        fontWeight: 'bold',
                        color: totalVariance > 0 ? '#EF4444' : totalVariance < 0 ? '#10B981' : '#6B7280'
                      }}
                    />
                  </div>

                  {/* Utilization */}
                  <div className="form-group">
                    <label className="form-label">
                      Utilization
                    </label>
                    <input
                      type="text"
                      value={`${utilizationPercentage}%`}
                      className="form-input"
                      readOnly
                      disabled
                      style={{ 
                        backgroundColor: '#f3f4f6', 
                        cursor: 'not-allowed', 
                        fontWeight: 'bold',
                        color: parseFloat(utilizationPercentage) >= 95 ? '#EF4444' : parseFloat(utilizationPercentage) >= 80 ? '#F59E0B' : '#10B981'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'additional' && (
              <div>
                <div className="section-header">
                  <h3>Additional Information</h3>
                </div>
                
                {/* Budget Thresholds */}
                <div className="section-header" style={{ marginTop: '16px', marginBottom: '8px' }}>
                  <h4>Budget Thresholds</h4>
                </div>
                <div className="grid-row-3">
                  {/* Warning Threshold */}
                  <div className="form-group">
                    <label htmlFor="budgetThresholds.warningThreshold" className="form-label">
                      Warning Threshold (%)
                    </label>
                    <input
                      id="budgetThresholds.warningThreshold"
                      type="number"
                      value={formData.budgetThresholds.warningThreshold}
                      onChange={(e) => handleInputChange('budgetThresholds.warningThreshold', e.target.value)}
                      className="form-input"
                      min="0"
                      max="100"
                      step="1"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Critical Threshold */}
                  <div className="form-group">
                    <label htmlFor="budgetThresholds.criticalThreshold" className="form-label">
                      Critical Threshold (%)
                    </label>
                    <input
                      id="budgetThresholds.criticalThreshold"
                      type="number"
                      value={formData.budgetThresholds.criticalThreshold}
                      onChange={(e) => handleInputChange('budgetThresholds.criticalThreshold', e.target.value)}
                      className="form-input"
                      min="0"
                      max="100"
                      step="1"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Notify on Threshold */}
                  <div className="form-group">
                    <label htmlFor="budgetThresholds.notifyOnThreshold" className="form-label">
                      <input
                        type="checkbox"
                        id="budgetThresholds.notifyOnThreshold"
                        checked={formData.budgetThresholds.notifyOnThreshold}
                        onChange={(e) => handleInputChange('budgetThresholds.notifyOnThreshold', e.target.checked)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                      />
                      Notify on Threshold
                    </label>
                  </div>
                </div>

                {/* Requires Approval */}
                <div className="grid-row-3">
                  <div className="form-group">
                    <label htmlFor="requiresApproval" className="form-label">
                      <input
                        type="checkbox"
                        id="requiresApproval"
                        checked={formData.requiresApproval}
                        onChange={(e) => handleInputChange('requiresApproval', e.target.checked)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                      />
                      Requires Approval
                    </label>
                  </div>
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter additional notes"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/budget-plannings/list')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Budget Plan' : 'Create Budget Plan'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Budget Planning Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default BudgetPlanningForm

